import React from "react";
import { motion } from "framer-motion";

/**
 * Faithful pixel-precise recreations of the SafetyDesk81 app screens as static
 * React components. The values and layout mirror what a logged-in consulente
 * sees; no invented features. Because these are real DOM (not raster PNGs)
 * they stay sharp on every device and can be gently animated.
 */

// ----- shared bits ----- //
const menu = [
    { label: "Panoramica", icon: "chart" },
    { label: "Elenco aziende", icon: "building" },
    { label: "Lavoratori", icon: "users" },
    { label: "Sicurezza sul lavoro", icon: "shield" },
    { label: "Sorveglianza sanitaria", icon: "heart" },
    { label: "Formazione", icon: "cap" },
    { label: "HACCP", icon: "leaf" },
    { label: "Sopralluoghi", icon: "clipboard" },
    { label: "Registri", icon: "book" },
    { label: "DPI", icon: "helmet" },
    { label: "Calendario", icon: "calendar" },
    { label: "Comunicazioni", icon: "bell" },
    { label: "Report", icon: "file" },
];

function MenuIcon({ name }) {
    const common = { width: 14, height: 14, fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
        case "chart":
            return <svg {...common} viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-6" /></svg>;
        case "building":
            return <svg {...common} viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2" /></svg>;
        case "users":
            return <svg {...common} viewBox="0 0 24 24"><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.9 3.1-7 7-7s7 3.1 7 7" /><circle cx="17" cy="9" r="2.5" /><path d="M22 19c0-2.5-2-5-5-5" /></svg>;
        case "shield":
            return <svg {...common} viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" /></svg>;
        case "heart":
            return <svg {...common} viewBox="0 0 24 24"><path d="M20.8 8.6c0 3.2-3.6 6.8-8.8 11-5.2-4.2-8.8-7.8-8.8-11C3.2 6 5.2 4 7.6 4c1.6 0 3 .8 4.4 2.4C13.4 4.8 14.8 4 16.4 4c2.4 0 4.4 2 4.4 4.6z" /></svg>;
        case "cap":
            return <svg {...common} viewBox="0 0 24 24"><path d="M12 3L2 8l10 5 10-5-10-5z" /><path d="M6 10v6c0 1.7 2.7 3 6 3s6-1.3 6-3v-6" /></svg>;
        case "leaf":
            return <svg {...common} viewBox="0 0 24 24"><path d="M4 20c8 0 16-6 16-16-8 0-16 6-16 16z" /><path d="M4 20l6-6" /></svg>;
        case "clipboard":
            return <svg {...common} viewBox="0 0 24 24"><rect x="6" y="4" width="12" height="17" rx="2" /><rect x="9" y="2" width="6" height="4" rx="1" /><path d="M9 11h6M9 15h6" /></svg>;
        case "book":
            return <svg {...common} viewBox="0 0 24 24"><path d="M4 4h6a3 3 0 0 1 3 3v13a3 3 0 0 0-3-3H4V4z" /><path d="M20 4h-6a3 3 0 0 0-3 3v13a3 3 0 0 1 3-3h6V4z" /></svg>;
        case "helmet":
            return <svg {...common} viewBox="0 0 24 24"><path d="M4 14a8 8 0 0 1 16 0v3H4v-3z" /><path d="M9 14V8c0-1 1-1 3-1s3 0 3 1v6" /></svg>;
        case "calendar":
            return <svg {...common} viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>;
        case "bell":
            return <svg {...common} viewBox="0 0 24 24"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5L6 16z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>;
        case "file":
            return <svg {...common} viewBox="0 0 24 24"><path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M14 3v6h6" /></svg>;
        default:
            return null;
    }
}

function Donut({ segments, total, size = 140, strokeWidth = 22 }) {
    // segments: [{value, color}]
    const r = (size - strokeWidth) / 2;
    const c = 2 * Math.PI * r;
    let offset = 0;
    const cx = size / 2;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
            <circle cx={cx} cy={cx} r={r} fill="none" stroke="#F1F4FA" strokeWidth={strokeWidth} />
            {segments.map((s, i) => {
                const dash = (s.value / total) * c;
                const el = (
                    <circle
                        key={i}
                        cx={cx}
                        cy={cx}
                        r={r}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${dash} ${c - dash}`}
                        strokeDashoffset={-offset}
                        transform={`rotate(-90 ${cx} ${cx})`}
                        strokeLinecap="butt"
                    />
                );
                offset += dash;
                return el;
            })}
            <text x="50%" y="49%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 22, fontWeight: 800, fontFamily: "Manrope", fill: "#012659" }}>
                {total}
            </text>
            <text x="50%" y="63%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, letterSpacing: 1.5, fill: "#5b6a8a", textTransform: "uppercase" }}>
                Totale
            </text>
        </svg>
    );
}

function StatCard({ label, value, tone, icon }) {
    const tones = {
        sky: { bg: "#E4ECFD", text: "#1B4CE0" },
        mint: { bg: "#DFF3E4", text: "#0E8E4E" },
        cream: { bg: "#FBF3D4", text: "#8C6E14" },
        peach: { bg: "#FDE3DD", text: "#B12D2D" },
        lav: { bg: "#EDE3FD", text: "#5F32C4" },
    };
    const t = tones[tone] || tones.sky;
    return (
        <div className="rounded-2xl p-4 flex items-start justify-between" style={{ background: t.bg }}>
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: t.text, opacity: 0.75 }}>
                    {label}
                </div>
                <div className="text-2xl md:text-3xl font-extrabold mt-1 font-display" style={{ color: t.text }}>
                    {value}
                </div>
            </div>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.55)", color: t.text }}>
                {icon}
            </div>
        </div>
    );
}

function AppShell({ children, current = "Panoramica", subtitle = "Centro Sicurezza Consulenze" }) {
    return (
        <div className="rounded-2xl overflow-hidden border border-white/40 bg-white shadow-card" style={{ boxShadow: "0 30px 80px -30px rgba(1,38,89,0.35)" }}>
            {/* browser chrome */}
            <div className="h-8 bg-navy-50 flex items-center gap-1.5 px-3 border-b border-navy-100">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <div className="mx-auto text-[10px] text-navy-400 font-mono truncate max-w-[60%]">safetydesk81.centrosicurezzaconsulenze.net/{current.toLowerCase().replace(/\s/g, "-")}</div>
            </div>
            <div className="flex" style={{ minHeight: 380 }}>
                {/* sidebar */}
                <aside className="w-[180px] bg-navy-900 text-white/80 p-3 hidden sm:block">
                    <div className="mb-3 px-1">
                        <div className="text-[10px] font-mono tracking-widest text-white/40">CONSULENTE</div>
                    </div>
                    <ul className="space-y-0.5">
                        {menu.map((m) => (
                            <li key={m.label}>
                                <span
                                    className={`flex items-center gap-2 text-[11px] px-2 py-1.5 rounded-md ${
                                        m.label === current
                                            ? "bg-gold-400 text-navy-900 font-semibold"
                                            : "text-white/75 hover:bg-white/5"
                                    }`}
                                >
                                    <MenuIcon name={m.icon} />
                                    <span className="truncate">{m.label}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </aside>
                {/* main */}
                <main className="flex-1 bg-white">
                    <div className="border-b border-navy-100 px-4 py-2.5 flex items-center justify-between">
                        <div className="text-xs">
                            <span className="text-navy-900 font-semibold uppercase tracking-widest">{current}</span>
                            <span className="mx-2 text-navy-200">/</span>
                            <span className="text-gold-500 font-medium">{subtitle}</span>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-navy-900 text-white text-[10px] font-bold flex items-center justify-center">G</div>
                    </div>
                    <div className="p-3 md:p-4 bg-navy-50">{children}</div>
                </main>
            </div>
        </div>
    );
}

// ---------------------------- SCREENS ---------------------------- //

export function DashboardMock({ animate = true }) {
    return (
        <AppShell current="Panoramica">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-3">
                <StatCard label="Aziende monitorate" value="189" tone="sky" icon={<MenuIcon name="building" />} />
                <StatCard label="Lavoratori" value="1300" tone="mint" icon={<MenuIcon name="users" />} />
                <StatCard label="Sopralluoghi" value="187" tone="cream" icon={<MenuIcon name="clipboard" />} />
                <StatCard label="Scadenze critiche" value="959" tone="peach" icon={<MenuIcon name="bell" />} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <motion.div
                    className="bg-white rounded-2xl p-4 border border-navy-100"
                    initial={animate ? { opacity: 0, y: 20 } : false}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-xs font-semibold text-navy-900 mb-2 flex items-center gap-1.5">
                        <span className="w-4 h-4 bg-mint rounded-md flex items-center justify-center text-emerald-700"><MenuIcon name="cap" /></span>
                        Corsi di formazione
                    </div>
                    <div className="flex items-center gap-3">
                        <Donut
                            total={2766}
                            segments={[
                                { value: 2311, color: "#10B981" },
                                { value: 6, color: "#FFCB05" },
                                { value: 449, color: "#EF4444" },
                            ]}
                            size={120}
                            strokeWidth={18}
                        />
                        <ul className="text-[11px] space-y-1 flex-1">
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Validi</span><b>2311 <span className="text-navy-400 font-normal">83.6%</span></b></li>
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-gold-400" /> In scadenza (30gg)</span><b>6 <span className="text-navy-400 font-normal">0.2%</span></b></li>
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-red-500" /> Scaduti</span><b>449 <span className="text-navy-400 font-normal">16.2%</span></b></li>
                        </ul>
                    </div>
                </motion.div>
                <motion.div
                    className="bg-white rounded-2xl p-4 border border-navy-100"
                    initial={animate ? { opacity: 0, y: 20 } : false}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-xs font-semibold text-navy-900 mb-2 flex items-center gap-1.5">
                        <span className="w-4 h-4 bg-peach rounded-md flex items-center justify-center text-red-700"><MenuIcon name="heart" /></span>
                        Visite mediche
                    </div>
                    <div className="flex items-center gap-3">
                        <Donut
                            total={1143}
                            segments={[
                                { value: 618, color: "#10B981" },
                                { value: 15, color: "#FFCB05" },
                                { value: 510, color: "#EF4444" },
                            ]}
                            size={120}
                            strokeWidth={18}
                        />
                        <ul className="text-[11px] space-y-1 flex-1">
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Valide</span><b>618 <span className="text-navy-400 font-normal">54.1%</span></b></li>
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-gold-400" /> In scadenza (30gg)</span><b>15 <span className="text-navy-400 font-normal">1.3%</span></b></li>
                            <li className="flex justify-between"><span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-red-500" /> Scadute</span><b>510 <span className="text-navy-400 font-normal">44.6%</span></b></li>
                        </ul>
                    </div>
                </motion.div>
            </div>
        </AppShell>
    );
}

export function FormazioneMock() {
    const rows = [
        ["Addetti ai lavori in ambienti confinati", "Andrea Nocchi", "15/05/2024", "15/05/2029", "Valido"],
        ["Addetti ai lavori in ambienti confinati", "Daniele Vannuccini", "08/10/2021", "08/10/2026", "Valido"],
        ["Antincendio rischio medio", "Marco Menconi", "02/03/2020", "02/03/2025", "Scaduto"],
        ["Preposto — Aggiornamento", "Paolo Terrosi", "11/06/2024", "11/06/2029", "Valido"],
        ["RSPP Modulo A", "Gianpiero Cappelli", "08/09/2022", "08/09/2027", "In scadenza"],
    ];
    const stateColor = { "Valido": "text-emerald-700 bg-mint", "Scaduto": "text-red-700 bg-peach", "In scadenza": "text-amber-800 bg-cream" };
    return (
        <AppShell current="Formazione">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-3">
                <StatCard label="Totale corsi" value="2767" tone="sky" icon={<MenuIcon name="cap" />} />
                <StatCard label="Validi" value="2312" tone="mint" icon={<MenuIcon name="cap" />} />
                <StatCard label="In scadenza" value="53" tone="cream" icon={<MenuIcon name="bell" />} />
                <StatCard label="Scaduti" value="402" tone="peach" icon={<MenuIcon name="bell" />} />
            </div>
            <div className="bg-white rounded-2xl border border-navy-100 overflow-hidden">
                <div className="px-3 py-2 flex flex-wrap gap-1.5 text-[10px] border-b border-navy-100">
                    {["Tutti", "Antincendio", "Primo soccorso", "HACCP", "RSPP / RLS", "Preposto", "Dirigente"].map((t, i) => (
                        <span key={t} className={`px-2 py-1 rounded-full font-medium ${i === 0 ? "bg-navy-900 text-white" : "bg-navy-50 text-navy-700"}`}>{t}</span>
                    ))}
                </div>
                <table className="w-full text-[11px]">
                    <thead className="bg-navy-50 text-navy-500 uppercase tracking-widest text-[9px]">
                        <tr>
                            <th className="text-left px-3 py-2">Corso</th>
                            <th className="text-left px-3 py-2">Lavoratore</th>
                            <th className="text-left px-3 py-2">Rilascio</th>
                            <th className="text-left px-3 py-2">Scadenza</th>
                            <th className="text-left px-3 py-2">Stato</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(([a, b, c, d, e]) => (
                            <tr key={a + b} className="border-t border-navy-100/60">
                                <td className="px-3 py-2 text-navy-900 font-medium">{a}</td>
                                <td className="px-3 py-2 text-navy-700 select-none" style={{ filter: "blur(5px)" }}>{b}</td>
                                <td className="px-3 py-2 text-navy-600">{c}</td>
                                <td className="px-3 py-2 text-navy-600">{d}</td>
                                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stateColor[e]}`}>{e}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </AppShell>
    );
}

export function CalendarMock() {
    const daysHeader = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
    const cells = [];
    // first row starts Wed 1
    for (let i = 0; i < 35; i++) cells.push(i);
    const events = {
        4: { label: "Revisione DVR", tone: "sky" },
        11: { label: "Antincendio", tone: "peach" },
        17: { label: "Visita medica", tone: "mint" },
        23: { label: "Sopralluogo", tone: "cream" },
        27: { label: "HACCP", tone: "lav" },
    };
    const eventTone = {
        sky: "bg-sky text-blue-800",
        peach: "bg-peach text-red-800",
        mint: "bg-mint text-emerald-800",
        cream: "bg-cream text-amber-800",
        lav: "bg-lav text-purple-800",
    };
    return (
        <AppShell current="Calendario">
            <div className="bg-white rounded-2xl border border-navy-100 p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-bold text-navy-900 font-display">Ottobre 2026</div>
                    <div className="flex gap-1">
                        <button className="w-6 h-6 rounded-md border border-navy-100 flex items-center justify-center text-navy-500"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 6l-6 6 6 6" /></svg></button>
                        <button className="w-6 h-6 rounded-md border border-navy-100 flex items-center justify-center text-navy-500"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg></button>
                    </div>
                </div>
                <div className="grid grid-cols-7 text-[9px] uppercase tracking-widest text-navy-400 mb-1">
                    {daysHeader.map((d) => <div key={d} className="px-1.5 py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {cells.map((i) => {
                        const day = i - 2; // start empty
                        const ev = events[day];
                        return (
                            <div key={i} className={`aspect-square border rounded-md p-1 text-[10px] ${day === 15 ? "border-gold-400 bg-gold-50" : "border-navy-100"}`}>
                                <div className={`text-navy-500 font-medium ${day === 15 ? "text-navy-900" : ""}`}>{day > 0 && day <= 31 ? day : ""}</div>
                                {ev && <div className={`mt-1 rounded-sm px-1 py-0.5 text-[8px] font-semibold ${eventTone[ev.tone]} truncate`}>{ev.label}</div>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </AppShell>
    );
}

export function ReportMock() {
    return (
        <AppShell current="Report">
            <div className="bg-white rounded-2xl border border-navy-100 p-4 mb-3">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-xl bg-sky text-blue-700 flex items-center justify-center"><MenuIcon name="file" /></div>
                    <div>
                        <div className="font-bold text-navy-900 text-sm font-display">Centro Report</div>
                        <div className="text-[10px] text-navy-500">Esporta in Excel tutte le scadenze per una singola azienda o per l'intero portafoglio.</div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <StatCard label="Formazione" value="2767" tone="mint" icon={<MenuIcon name="cap" />} />
                <StatCard label="Visite mediche" value="1143" tone="cream" icon={<MenuIcon name="heart" />} />
                <StatCard label="Sicurezza" value="11" tone="peach" icon={<MenuIcon name="shield" />} />
                <StatCard label="DPI" value="6" tone="sky" icon={<MenuIcon name="helmet" />} />
                <StatCard label="HACCP" value="5" tone="lav" icon={<MenuIcon name="leaf" />} />
                <StatCard label="Lavoratori" value="1300" tone="mint" icon={<MenuIcon name="users" />} />
            </div>
        </AppShell>
    );
}

export function SopralluoghiMock() {
    return (
        <AppShell current="Sopralluoghi">
            <div className="grid grid-cols-3 gap-2.5 mb-3">
                <StatCard label="Programmati" value="187" tone="cream" icon={<MenuIcon name="clipboard" />} />
                <StatCard label="Fatti" value="2" tone="mint" icon={<MenuIcon name="clipboard" />} />
                <StatCard label="Da fare" value="187" tone="sky" icon={<MenuIcon name="clipboard" />} />
            </div>
            <div className="bg-white rounded-2xl border border-navy-100 p-3">
                <div className="text-xs font-semibold text-navy-900 mb-2 font-display">Prossimi sopralluoghi</div>
                <ul className="text-[11px] divide-y divide-navy-100/60">
                    {[
                        ["Casa21 S.a.s.", "Sopralluogo semestrale", "18/09/2026"],
                        ["Podere Tiberini S.r.l.", "Ispezione HACCP", "22/09/2026"],
                        ["Centro Sicurezza S.r.l.", "Verifica DPI", "01/10/2026"],
                        ["Trattoria La Vetta", "Sopralluogo annuale", "10/10/2026"],
                    ].map(([az, tipo, data]) => (
                        <li key={az + tipo} className="py-2 flex items-center justify-between">
                            <div>
                                <div className="text-navy-900 font-medium select-none" style={{ filter: "blur(5px)" }}>{az}</div>
                                <div className="text-navy-500 text-[10px]">{tipo}</div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-navy-50 text-navy-700">{data}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </AppShell>
    );
}

// ---------- Registri (Buon Pane) ---------- //
export function RegistriMock() {
    return (
        <AppShell current="Registri" subtitle="Buon Pane S.r.l.">
            <div className="bg-white rounded-2xl border border-navy-100 p-3 md:p-4">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-sky text-blue-700 flex items-center justify-center">
                            <MenuIcon name="clipboard" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-navy-900 text-sm">Registri di autocontrollo</div>
                            <div className="text-[10px] text-navy-500">HACCP, Legionella, Piscine — compilazione, firma digitale, non conformità.</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="text-[10px] bg-navy-50 rounded-md px-2 py-1 text-navy-700 font-medium">Buon Pane S.r.l.</div>
                        <motion.a
                            aria-label="Dossier ASL PDF"
                            className="relative inline-flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-md bg-emerald-500 text-white ring-4 ring-emerald-300/50"
                            initial={{ scale: 0.94 }}
                            animate={{ scale: [1, 1.04, 1] }}
                            transition={{ repeat: Infinity, duration: 2.4 }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M14 3v6h6" /></svg>
                            Dossier ASL (PDF)
                            <span className="absolute -top-2 -right-2 bg-gold-400 text-navy-950 rounded-full text-[8px] font-bold px-1.5 py-0.5">NEW</span>
                        </motion.a>
                        <span className="text-[10px] font-semibold px-3 py-1.5 rounded-md bg-gold-100 text-gold-700">⚙ Attiva Registri</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    <StatCard label="Da compilare" value="12" tone="cream" icon={<MenuIcon name="bell" />} />
                    <StatCard label="Completati" value="47" tone="mint" icon={<MenuIcon name="clipboard" />} />
                    <StatCard label="Scaduti" value="0" tone="peach" icon={<MenuIcon name="bell" />} />
                    <StatCard label="Non conformi" value="0" tone="cream" icon={<MenuIcon name="shield" />} />
                    <StatCard label="NC aperte" value="0" tone="sky" icon={<MenuIcon name="file" />} />
                </div>

                <div className="border-b border-navy-100 flex gap-1 mb-3">
                    {["HACCP", "Legionella", "Piscine"].map((t, i) => (
                        <span key={t} className={`text-[11px] font-semibold px-3 py-2 rounded-t-md ${i === 0 ? "bg-navy-950 text-white" : "text-navy-500"}`}>
                            {t}
                            <span className="ml-1 text-[9px] opacity-60">{i === 0 ? "12" : "0"}</span>
                        </span>
                    ))}
                </div>

                <ul className="text-[11px] space-y-1.5">
                    {[
                        ["Registro temperature celle frigo", "Compilazione giornaliera", "Aggiornato", "mint"],
                        ["Registro sanificazione ambienti", "Compilazione settimanale", "Da compilare", "cream"],
                        ["Registro derattizzazione", "Report trimestrale disponibile", "Dossier PDF", "sky"],
                        ["Non conformità (NC)", "Nessuna aperta", "Nessuna", "mint"],
                    ].map(([t, d, s, tone]) => (
                        <li key={t} className="flex items-center justify-between px-3 py-2 rounded-lg bg-navy-50/60 border border-navy-100/60">
                            <div>
                                <div className="text-navy-900 font-medium">{t}</div>
                                <div className="text-navy-500 text-[10px]">{d}</div>
                            </div>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                tone === "mint" ? "bg-mint text-emerald-700" :
                                tone === "cream" ? "bg-cream text-amber-800" :
                                "bg-sky text-blue-700"
                            }`}>{s}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </AppShell>
    );
}

// ---------- AI Chat side card ---------- //
export function AiChatMock() {
    return (
        <div className="rounded-3xl bg-white border border-navy-100 shadow-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-navy-100 bg-gradient-to-r from-navy-950 to-navy-800 text-white">
                <div className="w-8 h-8 rounded-full bg-gold-400 text-navy-950 flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="7" width="16" height="12" rx="3" /><circle cx="9" cy="13" r="1" fill="currentColor" /><circle cx="15" cy="13" r="1" fill="currentColor" /><path d="M9 17h6M12 3v4" /></svg>
                </div>
                <div>
                    <div className="text-xs font-bold">Agente AI SafetyDesk81</div>
                    <div className="text-[10px] text-white/60 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                    </div>
                </div>
            </div>
            <div className="p-4 space-y-3 bg-navy-50/40">
                <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-navy-100 px-3 py-2 text-[12px] text-navy-800 shadow-sm">
                    Ciao, sono l'agente AI di SafetyDesk81. Posso aiutarti a consultare scadenze, documenti e adempimenti.
                </div>
                <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-navy-950 text-white px-3 py-2 text-[12px] shadow-sm">
                    Quali corsi scadono nei prossimi 30 giorni per Buon Pane?
                </div>
                <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-navy-100 px-3 py-2 text-[12px] text-navy-800 shadow-sm">
                    In scadenza entro 30 giorni per <b>Buon Pane S.r.l.</b>:
                    <ul className="mt-1.5 space-y-1 text-[11px] text-navy-700">
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold-500" /> Antincendio rischio medio — <span className="text-navy-500">15/10/2026</span></li>
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold-500" /> Primo soccorso — <span className="text-navy-500">28/10/2026</span></li>
                        <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold-500" /> HACCP addetti — <span className="text-navy-500">02/11/2026</span></li>
                    </ul>
                    <div className="mt-2 text-[10px] text-navy-500 italic">Vuoi che prepari la comunicazione ai lavoratori?</div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 rounded-full border border-navy-100 bg-white px-3 py-2 text-[11px] text-navy-400">Chiedi qualcosa all'agente…</div>
                    <div className="w-8 h-8 rounded-full bg-gold-400 flex items-center justify-center text-navy-950">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                    </div>
                </div>
            </div>
            <div className="px-4 py-2 border-t border-navy-100 text-[9px] font-mono text-navy-400 uppercase tracking-widest text-center">
                Anteprima funzionalità AI · in arrivo con SafetyDesk81
            </div>
        </div>
    );
}
